import { IAction } from "../../../base/common/actions";
import { Emitter, Event } from "../../../base/common/events";
import { Disposable } from "../../../base/common/lifecycle";
import { localize, localize2 } from "../../../base/common/nls";
import { getFlatContextMenuActions } from "../../../editor/services/titlebar/menuEntryActionViewItem";
import { getIconId } from "../../action/common/action";
import { IMenuService, isIMenuItem, MenuId, MenuItemAction, MenuRegistry, SubmenuItemAction } from "../../actions/common/actions";
import { ICommandService } from "../../commands/common/commands";
import { IKeybindingService } from "../../keybinding/common/keybindingService";
import { IContextKeyService } from "../../contextkey/common/contextkey";
import { ContextMenuContext, IContextMenuItem, IContextMenuService, IContextMenuSubItem, WillShowContextMenuArguments } from "./context";



export class ContextMenuService extends Disposable implements IContextMenuService {

    // private static _willShowContextMenu = new Emitter<WillShowContextMenuArguments>();
    // static onWillShowContextMenu: Event<WillShowContextMenuArguments> = this._willShowContextMenu.event;

    readonly _serviceBrand: undefined;

    constructor(
        @IMenuService private readonly menuService: IMenuService,
        @IContextKeyService private readonly contextKeyService: IContextKeyService,
        @IKeybindingService private readonly keybindingService: IKeybindingService,
        @ICommandService private readonly commandService: ICommandService,
    ) {
        super();
    }


    private readonly _onWillShowContextMenu = this._register(new Emitter<WillShowContextMenuArguments>());
    public readonly onWillShowContextMenu = this._onWillShowContextMenu.event;

    private readonly _onDidRequestContextMenu = this._register(new Emitter<ContextMenuContext>());
    public readonly onDidRequestContextMenu = this._onDidRequestContextMenu.event;

    requestContextMenu(context: ContextMenuContext): void {
        this._onDidRequestContextMenu.fire(context);
    }

    getContextMenuItems(context: ContextMenuContext): Array<IContextMenuItem | IContextMenuSubItem> {

        // ContextMenuService._willShowContextMenu.fire({ context });
        this._onWillShowContextMenu.fire({ context });

        const groups = this.menuService.getMenuActions(context.menuId, this.contextKeyService);
        const actions = getFlatContextMenuActions(groups);
        const result = new Array<IContextMenuItem | IContextMenuSubItem>();
        this.populateMenuItems(actions, result, context);
        // result.push({
        //     label: "test",
        //     icon: undefined,
        //     action: () => {},
        // }, {
        //     label: "submenu",
        //     icon: "file",
        //     subItems: [{
        //         label: "subtest",
        //         icon: undefined,
        //         action: () => {}
        //     }]
        // })
        return result;
    }

    private populateMenuItems(menuItems: readonly IAction[], array: Array<IContextMenuItem | IContextMenuSubItem>, context: ContextMenuContext): void {
        for (const menuItem of menuItems) {
            if (menuItem instanceof MenuItemAction || menuItem instanceof SubmenuItemAction) {
                if (menuItem instanceof MenuItemAction) {
                    // Preconditions decide whether the item belongs in this menu.
                    // Unavailable commands stay hidden unless they opt into a disabled row.
                    if (!menuItem.enabled) continue;
                    const canExecute = this.commandService.canExecuteCommand(menuItem.id, context).canExecute;
                    if (!canExecute && !menuItem.item.showWhenDisabled) continue;
                    array.push({
                        label: menuItem.label,
                        icon: menuItem.item?.icon ? getIconId(menuItem.item.icon) : undefined,
                        tooltip: menuItem.tooltip,
                        enabled: menuItem.enabled && canExecute,
                        keybinding: resolveMenuKeybinding(
                            id => this.keybindingService?.lookupKeybinding(id, this.contextKeyService),
                            menuItem.id,
                        ),
                        action: () => {
                            menuItem.run(context);
                        }
                    });
                } else if (menuItem instanceof SubmenuItemAction) {
                    if (!menuItem.enabled) continue;
                    const subItem: IContextMenuSubItem = {
                        label: menuItem.label,
                        icon: menuItem.item?.icon ? getIconId(menuItem.item.icon) : undefined,
                        enabled: menuItem.enabled,
                        subItems: [],
                    };
                    this.populateMenuItems(menuItem.actions, subItem.subItems, context);
                    // every child hidden → the stub offers nothing
                    if (subItem.subItems.length === 0) continue;
                    array.push(subItem);
                }
            }
        }
    }
}

/**
 * The shortcut label to show beside a context-menu entry, or undefined.
 *
 * Menu entries showed no shortcut even for Copy/Paste, which really are bound to
 * Ctrl+C / Ctrl+V (needleEditor.ts). The bindings worked; nothing ever looked
 * them up, because IContextMenuItem had nowhere to put one.
 *
 * Undefined rather than "" for "none", so the UI skips the gutter instead of
 * reserving a blank column, and never throws: a shortcut is decoration, and
 * failing to find one must not cost the user the whole menu.
 */
export function resolveMenuKeybinding(
    lookup: (commandId: string) => { getLabel(): string } | undefined,
    commandId: string | undefined,
): string | undefined {
    if (!commandId) return undefined;
    try {
        const label = lookup(commandId)?.getLabel();
        return label && label.trim().length > 0 ? label : undefined;
    }
    catch {
        return undefined;
    }
}
